// src/components/layout/Navbar/Navbar.jsx

import React, { useState, useEffect } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import event from "../../../assets/site-images/eventfullsunday.png";
import { useSelector, useDispatch } from "react-redux";
import { AnimatePresence, motion } from "framer-motion";
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { 
  IoMenu, 
  IoClose, 
  IoPersonCircle, 
  IoBusinessOutline 
} from "react-icons/io5";
import { 
  FaTicketAlt, 
  FaUserTie, 
  FaBars, 
  FaUser,
  FaBell,
  FaLock,
  FaCog,
  FaSignOutAlt,
  FaBook,
  FaPlusCircle,
} from "react-icons/fa";
import { BiSolidPurchaseTag } from "react-icons/bi";
import { HiOutlineExternalLink } from "react-icons/hi";
import { fetchOrganiserEvents } from "../../../redux/OrganiserEvent/organiserEventAction/GetOrganiserEventAction";
import { toast } from 'react-toastify';

// Navbar component for the website
const Navbar = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isDashboardMenuOpen, setIsDashboardMenuOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  
  const searchParams = new URLSearchParams(location.search);
  const currentSection = searchParams.get('section');

  const linkTo = window.location.hostname === "localhost" ? "/" : "https://organizer.eventfullsunday.co.uk/";

  const profileState = useSelector((state) => state.profile);
  const firstName = profileState?.profile?.full_name?.split(" ")[0];
  const token = localStorage.getItem("access") || sessionStorage.getItem("access");

  const toggleMenu = () => setIsMenuOpen(!isMenuOpen);
  const toggleDashboardMenu = () => setIsDashboardMenuOpen(!isDashboardMenuOpen);
  
  // Close mobile menu on route change
  useEffect(() => {
    setIsMenuOpen(false);
    setIsDashboardMenuOpen(false);
  }, [location.pathname]);

  // Navigate to profile section in dashboard
  const navigateToProfile = () => {
    navigate("/dashboard", { state: { selectedSection: "profile" } });
  };

  // Direct navigation to the ManageEvents component with guaranteed refresh
  const navigateToManageEvents = () => {
    // Force refresh data first to ensure latest events are loaded
    if (profileState?.profile?.id) {
      dispatch(fetchOrganiserEvents(profileState.profile.id));
    }
    
    // If already on the manage-events section, force reload the page
    if (location.pathname === "/dashboard" && currentSection === "manage-events") {
      navigate(`/dashboard?section=manage-events&t=${Date.now()}`, { replace: true });
    } else {
      // Regular navigation to manage-events
      navigate("/dashboard?section=manage-events", { replace: true });
    }
  };

  const handleSectionClick = (section) => {
    if (section === "create-event") {
      if (profileState?.profile?.company_name === "") {
        toast.error('You need to update your profile with your business information to create events.');
      } else {
        navigate("/createEventForm");
      }
    } else if (section === "guide") {
      window.open("https://events.eventfullsunday.co.uk/", "_blank");
    } else if (section === "userend") {
      window.open("https://eventfullsunday.co.uk", "_blank");
    } else {
      // Update URL without page reload
      navigate(`/dashboard?section=${section}`, { replace: true });
    }
    
    setIsDashboardMenuOpen(false);
  };

  const handleLogout = () => {
    // Get the refresh token
    const refreshToken = localStorage.getItem("refresh") || sessionStorage.getItem("refresh");
    
    // Call the logout API endpoint
    fetch(`${process.env.REACT_APP_API_URL || 'http://127.0.0.1:8000'}/logout/`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ refresh: refreshToken }),
    })
    .then(() => {
      // Clear tokens
      localStorage.removeItem("access");
      localStorage.removeItem("refresh");
      sessionStorage.removeItem("access");
      sessionStorage.removeItem("refresh");
      
      // Force navigation to login
      window.location.href = '/login';
    })
    .catch(() => {
      // Clear tokens anyway even if API fails
      localStorage.removeItem("access");
      localStorage.removeItem("refresh");
      sessionStorage.removeItem("access");
      sessionStorage.removeItem("refresh");
      
      // Force navigation to login
      window.location.href = '/login';
    });
  };

  return (
    <nav className="bg-white shadow-md sticky top-0 z-50">
      <div className="max-w-[1400px] mx-auto px-4 lg:px-6">
        <div className="flex justify-between items-center py-3">
          {/* Dashboard Menu Button (shows when logged in) */}
          {token && (
            <button 
              onClick={toggleDashboardMenu}
              className="md:hidden p-2 px-3 bg-[#FF8100] text-white rounded-lg shadow-sm hover:bg-[#e67700] transition-all duration-300 flex items-center mr-3"
              aria-label="Dashboard Menu"
            >
              <FaBars className="h-4 w-4 mr-1" />
              <span className="text-xs font-medium"></span>
            </button>
          )}

          {/* Logo with Organizer Portal Icon */}
          <Link to={linkTo} className="flex items-center">
            <img src={event} className="h-12 w-auto" alt="Eventfull Sunday" />
            <div className="hidden sm:flex items-center ml-3 border-l pl-3 border-gray-200">
              <IoBusinessOutline className="text-[#0875E1] text-xl mr-2" />
              <div>
                <p className="text-sm font-bold text-[#004B95] uppercase tracking-wider">Organizer Portal</p>
                <p className="text-[10px] text-gray-500">Manage your Events and Tickets</p>
              </div>
            </div>
          </Link>

          {/* Only show mobile menu button if not logged in */}
          {!token && (
            <button 
              onClick={toggleMenu}
              className="p-2 rounded-md text-gray-700 md:hidden focus:outline-none"
            >
              {isMenuOpen ? (
                <IoClose size={24} />
              ) : (
                <IoMenu size={24} />
              )}
            </button>
          )}

          {/* Desktop Nav Links - Only show if not logged in */}
          {!token && (
            <div className="hidden md:flex items-center space-x-6">
              <a
                href="https://events.eventfullsunday.co.uk/"
                target="_blank"
                rel="noopener noreferrer"
                className="font-medium text-gray-700 hover:text-[#0875E1] transition-colors text-sm"
              >
                ORGANIZER GUIDE
              </a>
              <Link
                to="/login"
                className="font-medium text-gray-700 hover:text-[#0875E1] transition-colors text-sm"
              >
                Login
              </Link>
              <Link
                to="/signup"
                className="px-4 py-2 rounded-lg bg-[#0875E1] text-white hover:bg-[#0767c9] transition-all duration-200 text-sm font-medium shadow-sm"
              >
                Sign Up
              </Link>
            </div>
          )}

          {/* Show user info if logged in - with event and profile icons */}
          {token && (
            <div className="flex items-center space-x-1 sm:space-x-4">
              {/* Event Icon - Direct link to Manage Events page */}
              <button 
                onClick={navigateToManageEvents}
                className={`flex items-center transition-all duration-200 p-2 rounded-lg hover:bg-gray-100 ${
                  currentSection === "manage-events" ? "bg-[#0875E1]/10 text-[#0875E1]" : "text-[#565656]"
                }`}
                aria-label="Manage Events"
              >
                <FaTicketAlt size={18} className="flex-shrink-0" />
                <span className="ml-2 text-sm font-medium hidden lg:inline">
                  My Events
                </span>
              </button>
              
              {/* User Profile - Redirects to Profile with expanded menu */}
              <button 
                onClick={navigateToProfile}
                className={`flex items-center transition-all duration-200 p-2 rounded-lg hover:bg-gray-100 ${
                  (!currentSection || currentSection === "profile") ? "bg-[#0875E1]/10 text-[#0875E1]" : "text-[#565656]"
                }`}
                aria-label="Profile"
              >
                <FaUserTie size={18} className="flex-shrink-0" />
                <span className="ml-2 text-sm font-medium hidden sm:inline">
                  {firstName || 'Profile'}
                </span>
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Login/Signup Mobile Menu */}
      <AnimatePresence>
        {isMenuOpen && !token && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="md:hidden bg-white border-t border-gray-100 shadow-inner"
          >
            <div className="px-4 py-3 space-y-4">
              <a
                href="https://events.eventfullsunday.co.uk/"
                target="_blank"
                rel="noopener noreferrer"
                className="block py-2 font-medium text-gray-700"
              >
                ORGANIZER GUIDE
              </a>
              <div className="flex flex-col space-y-3">
                <Link
                  to="/login"
                  className="block py-2 text-center rounded-lg border border-[#0875E1] text-[#0875E1]"
                >
                  Login
                </Link>
                <Link
                  to="/signup"
                  className="block py-2 text-center rounded-lg bg-[#0875E1] text-white"
                >
                  Sign Up
                </Link>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Dashboard Mobile Menu */}
      <AnimatePresence>
        {isDashboardMenuOpen && token && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="fixed inset-0 bg-black bg-opacity-50 z-50 md:hidden"
            onClick={() => setIsDashboardMenuOpen(false)}
          >
            <motion.div
              initial={{ x: -300 }}
              animate={{ x: 0 }}
              exit={{ x: -300 }}
              transition={{ type: "tween" }}
              className="absolute top-0 left-0 w-[280px] h-full bg-white shadow-xl overflow-y-auto"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Menu Header */}
              <div className="flex items-center justify-between p-4 border-b border-gray-200">
                <h2 className="text-lg font-semibold text-[#004B95]">Dashboard</h2>
                <button 
                  onClick={() => setIsDashboardMenuOpen(false)}
                  className="p-2 rounded-full hover:bg-gray-100"
                >
                  <IoClose size={20} />
                </button>
              </div>
              
              {/* Menu Items */}
              <div className="py-2">
                <button
                  onClick={() => handleSectionClick("profile")}
                  className={`flex items-center w-full p-3 text-left ${
                    (!currentSection || currentSection === "profile") 
                      ? "bg-[#0875E1] text-white" 
                      : "text-gray-700 hover:bg-gray-100"
                  }`}
                >
                  <FaUser className="w-5 h-5 mr-3" />
                  <span className="font-medium">Profile</span>
                </button>
                
                <button
                  onClick={() => handleSectionClick("create-event")}
                  className="flex items-center w-full p-3 text-left text-gray-700 hover:bg-gray-100"
                >
                  <FaPlusCircle className="w-5 h-5 mr-3" />
                  <span className="font-medium">Create Event</span>
                </button>
                
                <button
                  onClick={() => handleSectionClick("manage-events")}
                  className={`flex items-center w-full p-3 text-left ${
                    currentSection === "manage-events" 
                      ? "bg-[#0875E1] text-white" 
                      : "text-gray-700 hover:bg-gray-100"
                  }`}
                >
                  <FaTicketAlt className="w-5 h-5 mr-3" />
                  <span className="font-medium">Manage Events</span>
                </button>
                
                <button
                  onClick={() => handleSectionClick("purchase-logs")}
                  className={`flex items-center w-full p-3 text-left ${
                    currentSection === "purchase-logs" 
                      ? "bg-[#0875E1] text-white" 
                      : "text-gray-700 hover:bg-gray-100"
                  }`}
                >
                  <BiSolidPurchaseTag className="w-5 h-5 mr-3" />
                  <span className="font-medium">Order Management</span>
                </button>
                
                <button
                  onClick={() => handleSectionClick("updates")}
                  className={`flex items-center w-full p-3 text-left ${
                    currentSection === "updates" 
                      ? "bg-[#0875E1] text-white" 
                      : "text-gray-700 hover:bg-gray-100"
                  }`}
                >
                  <FaBell className="w-5 h-5 mr-3" />
                  <span className="font-medium">Updates</span>
                </button>
                
                <button
                  onClick={() => handleSectionClick("guide")}
                  className="flex items-center w-full p-3 text-left text-gray-700 hover:bg-gray-100"
                >
                  <FaBook className="w-5 h-5 mr-3" />
                  <span className="font-medium">Organizer Guide</span>
                </button>
                
                <button
                  onClick={() => handleSectionClick("userend")}
                  className="flex items-center w-full p-3 text-left text-gray-700 hover:bg-gray-100"
                >
                  <HiOutlineExternalLink className="w-5 h-5 mr-3" />
                  <span className="font-medium">User Portal</span>
                </button>
                
                <button
                  onClick={() => handleSectionClick("privacy-policy")}
                  className={`flex items-center w-full p-3 text-left ${
                    currentSection === "privacy-policy" 
                      ? "bg-[#0875E1] text-white" 
                      : "text-gray-700 hover:bg-gray-100"
                  }`}
                >
                  <FaLock className="w-5 h-5 mr-3" />
                  <span className="font-medium">Privacy Policy</span>
                </button>
                
                <button
                  onClick={() => handleSectionClick("settings")}
                  className={`flex items-center w-full p-3 text-left ${
                    currentSection === "settings" 
                      ? "bg-[#0875E1] text-white" 
                      : "text-gray-700 hover:bg-gray-100"
                  }`}
                >
                  <FaCog className="w-5 h-5 mr-3" />
                  <span className="font-medium">Settings</span>
                </button>
                
                {/* Logout Button */}
                <div className="border-t border-gray-200 mt-4 pt-2">
                  <button
                    onClick={handleLogout}
                    className="flex items-center w-full p-3 text-left text-red-500 hover:bg-red-50"
                  >
                    <FaSignOutAlt className="w-5 h-5 mr-3" />
                    <span className="font-medium">Logout</span>
                  </button>
                </div>
              </div>
              
              {/* Copyright */}
              <div className="absolute bottom-0 left-0 right-0 p-4 text-center border-t border-gray-200">
                <p className="text-xs text-gray-500">
                  &copy; 2024-2025 eventfullsundays.co.uk
                </p>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      <ToastContainer position="top-right" autoClose={5000} />
    </nav>
  );
};

export default Navbar;